import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material'
import {
  Edit,
  Delete,
  Search,
  Add,
  Visibility,
  Description,
  PictureAsPdf,
  GetApp
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const ProcedimientosList = () => {
  const { user } = useAuth()
  const [procedimientos, setProcedimientos] = useState([])
  const [gestiones, setGestiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedGestion, setSelectedGestion] = useState('')
  const [selectedEstado, setSelectedEstado] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // Cargar procedimientos (con límite máximo permitido)
      const procResponse = await axios.get('/api/procedimientos?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setProcedimientos(procResponse.data?.procedimientos || [])

      // Cargar gestiones (con límite máximo permitido)
      const gestResponse = await axios.get('/api/gestiones?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      })
      // El endpoint retorna un array directo
      setGestiones(Array.isArray(gestResponse.data) ? gestResponse.data : [])
    } catch (error) {
      console.error('Error al cargar datos:', error)
      setError('Error al cargar datos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (procedimiento) => {
    if (!window.confirm(`¿Está seguro de eliminar el procedimiento "${procedimiento.titulo}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/procedimientos/${procedimiento.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      loadData()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleExportPDF = async (procedimiento) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`/api/export/procedimientos/${procedimiento.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${procedimiento.codigo}_${procedimiento.titulo}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      setError('Error al exportar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success'
      case 'revision': return 'warning'
      case 'borrador': return 'info'
      default: return 'default'
    }
  }

  const filteredProcedimientos = procedimientos.filter(proc => {
    const matchesSearch = proc.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proc.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proc.proceso.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesGestion = !selectedGestion || proc.gestion_id == selectedGestion
    const matchesEstado = !selectedEstado || proc.estado === selectedEstado
    
    return matchesSearch && matchesGestion && matchesEstado
  })

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando procedimientos...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Description sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Procedimientos
          </Typography>
        </Box>
        {(user?.rol === 'administrador' || user?.rol === 'editor' || user?.rol === 'consultor') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => window.location.href = '/procedimientos/nuevo'}
          >
            Nuevo Procedimiento
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar por título, código o proceso..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Gestión</InputLabel>
            <Select
              value={selectedGestion}
              label="Gestión"
              onChange={(e) => setSelectedGestion(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {gestiones.map((gestion) => (
                <MenuItem key={gestion.id} value={gestion.id}>
                  {gestion.codigo} - {gestion.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={selectedEstado}
              label="Estado"
              onChange={(e) => setSelectedEstado(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="borrador">BORRADOR</MenuItem>
              <MenuItem value="revision">REVISION</MenuItem>
              <MenuItem value="aprobado">APROBADO</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Proceso</TableCell>
              <TableCell>Gestión</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Versión</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProcedimientos.map((procedimiento) => (
              <TableRow key={procedimiento.id} hover>
                <TableCell>
                  <Chip
                    label={procedimiento.codigo}
                    size="small"
                    sx={{
                      backgroundColor: procedimiento.gestion_color || '#64B5F6',
                      color: '#fff',
                      fontWeight: 'bold',
                      border: `2px solid ${procedimiento.gestion_color || '#64B5F6'}`,
                      '&:hover': {
                        opacity: 0.9
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {procedimiento.titulo}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {procedimiento.proceso}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={procedimiento.gestion_codigo || 'N/A'}
                    size="small"
                    sx={{
                      backgroundColor: `${procedimiento.gestion_color || '#64B5F6'}20`,
                      color: procedimiento.gestion_color || '#64B5F6',
                      border: `1px solid ${procedimiento.gestion_color || '#64B5F6'}`,
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={procedimiento.estado.toUpperCase()}
                    color={getEstadoColor(procedimiento.estado)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    v{procedimiento.version}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => window.location.href = `/procedimientos/${procedimiento.id}`}
                      title="Ver"
                    >
                      <Visibility />
                    </IconButton>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleExportPDF(procedimiento)}
                      title="Exportar PDF"
                      color="secondary"
                    >
                      <PictureAsPdf />
                    </IconButton>

                    {(user?.rol === 'administrador' || user?.rol === 'editor') && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => window.location.href = `/procedimientos/${procedimiento.id}/editar`}
                          title="Editar"
                        >
                          <Edit />
                        </IconButton>
                        
                        {user?.rol === 'administrador' && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(procedimiento)}
                            title="Eliminar"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredProcedimientos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron procedimientos
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ProcedimientosList